import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/ConfirmModal';

const PricingPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'USD',
    maxVoters: '',
    maxPositions: '',
    durationDays: '',
    multiVotingEnabled: false,
    customBrandingEnabled: false,
    emailNotificationsEnabled: true,
    supportLevel: 'basic',
    advancedAnalyticsEnabled: false,
    packageType: 'per_election',
    displayOrder: 0,
  });
  const [currencyChanged, setCurrencyChanged] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/pricing-packages/all`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log('Packages response:', response.data);
      setPackages(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load pricing packages');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Track currency changes
    if (name === 'currency' && editingPackage && value !== editingPackage.currency) {
      setCurrencyChanged(true);
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : 0,
        maxVoters: formData.maxVoters ? parseInt(formData.maxVoters) : null,
        maxPositions: formData.maxPositions ? parseInt(formData.maxPositions) : null,
        durationDays: formData.durationDays ? parseInt(formData.durationDays) : 30,
        displayOrder: formData.displayOrder ? parseInt(formData.displayOrder) : 0,
      };

      if (editingPackage) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/pricing-packages/${editingPackage.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Package updated successfully!');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/pricing-packages`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Package created successfully!');
      }

      setShowModal(false);
      resetForm();
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save package');
    }
  };

  const handleEditClick = (pkg) => {
    setEditingPackage(pkg);
    setCurrencyChanged(false);
    setFormData({
      name: pkg.name,
      description: pkg.description || '',
      price: pkg.price,
      currency: pkg.currency || 'USD',
      maxVoters: pkg.maxVoters || '',
      maxPositions: pkg.maxPositions || '',
      durationDays: pkg.durationDays,
      multiVotingEnabled: pkg.multiVotingEnabled,
      customBrandingEnabled: pkg.customBrandingEnabled,
      emailNotificationsEnabled: pkg.emailNotificationsEnabled,
      supportLevel: pkg.supportLevel,
      advancedAnalyticsEnabled: pkg.advancedAnalyticsEnabled,
      packageType: pkg.packageType || 'per_election',
      displayOrder: pkg.displayOrder,
    });
    setShowModal(true);
  };

  const handleDeleteClick = (pkg) => {
    setPackageToDelete(pkg);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/pricing-packages/${packageToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Package deleted successfully!');
      setShowDeleteModal(false);
      setPackageToDelete(null);
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete package');
    }
  };

  const handleToggleStatus = async (pkg) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/pricing-packages/${pkg.id}/toggle-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Package ${pkg.isActive ? 'deactivated' : 'activated'} successfully!`);
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to toggle package status');
    }
  };

  const resetForm = () => {
    setEditingPackage(null);
    setCurrencyChanged(false);
    setFormData({
      name: '',
      description: '',
      price: '',
      currency: packages.length > 0 ? packages[0].currency : 'USD',
      maxVoters: '',
      maxPositions: '',
      durationDays: '',
      multiVotingEnabled: false,
      customBrandingEnabled: false,
      emailNotificationsEnabled: true,
      supportLevel: 'basic',
      advancedAnalyticsEnabled: false,
      packageType: 'per_election',
      displayOrder: 0,
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header bg-white d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1.125rem' }}>Pricing Packages</h5>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-sm"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '0.375rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add Package
          </button>
        </div>

        <div className="card-body">
          {packages.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-tags" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No pricing packages found. Create your first package.</p>
            </div>
          ) : (
            <>
              {/* Per-Election Packages */}
              {packages.filter(p => p.packageType !== 'annual').length > 0 && (
                <>
                  <h6 className="mb-3" style={{ fontWeight: '600', fontSize: '0.875rem', color: '#4b5563' }}>
                    <i className="bi bi-calendar-event me-2"></i>Per-Election Packages
                  </h6>
                  <div className="row g-4 mb-4">
                    {packages.filter(p => p.packageType !== 'annual').map((pkg) => (
                      <div key={pkg.id} className="col-md-4">
                        <div className={`card h-100 ${!pkg.isActive ? 'opacity-50' : ''}`} style={{ borderLeft: `4px solid ${pkg.isActive ? '#667eea' : '#9ca3af'}` }}>
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h5 className="mb-0" style={{ fontWeight: '600' }}>{pkg.name}</h5>
                                <small className="text-muted">Order: {pkg.displayOrder}</small>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <button onClick={() => handleEditClick(pkg)} className="btn btn-sm btn-link p-0" title="Edit" style={{ color: '#667eea' }}>
                                  <i className="bi bi-pencil" style={{ fontSize: '1.1rem' }}></i>
                                </button>
                                <button onClick={() => handleToggleStatus(pkg)} className="btn btn-sm btn-link p-0" title={pkg.isActive ? 'Deactivate' : 'Activate'} style={{ color: pkg.isActive ? '#f59e0b' : '#10b981' }}>
                                  <i className={`bi ${pkg.isActive ? 'bi-pause-circle' : 'bi-play-circle'}`} style={{ fontSize: '1.1rem' }}></i>
                                </button>
                                <button onClick={() => handleDeleteClick(pkg)} className="btn btn-sm btn-link p-0" title="Delete" style={{ color: '#ef4444' }}>
                                  <i className="bi bi-trash" style={{ fontSize: '1.1rem' }}></i>
                                </button>
                                <span className={`badge ${pkg.isActive ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.75rem' }}>{pkg.isActive ? 'Active' : 'Inactive'}</span>
                              </div>
                            </div>
                            <div className="mb-3"><div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#667eea' }}>{formatCurrency(pkg.price, pkg.currency)}</div></div>
                            <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>{pkg.description || 'No description'}</p>
                            <div style={{ fontSize: '0.875rem' }}>
                              <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>{pkg.maxVoters ? `Up to ${pkg.maxVoters} voters` : 'Unlimited voters'}</div>
                              <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>{pkg.maxPositions ? `Up to ${pkg.maxPositions} positions` : 'Unlimited positions'}</div>
                              <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>{pkg.durationDays}-day active period</div>
                              {pkg.multiVotingEnabled && <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>Multi-voting</div>}
                              {pkg.customBrandingEnabled && <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>Custom branding</div>}
                              {pkg.advancedAnalyticsEnabled && <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>Advanced analytics</div>}
                              <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>{pkg.supportLevel.charAt(0).toUpperCase() + pkg.supportLevel.slice(1)} support</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Annual Subscription Packages */}
              {packages.filter(p => p.packageType === 'annual').length > 0 && (
                <>
                  <h6 className="mb-3" style={{ fontWeight: '600', fontSize: '0.875rem', color: '#4b5563' }}>
                    <i className="bi bi-calendar-check me-2"></i>Annual Subscription Packages
                  </h6>
                  <div className="row g-4">
                    {packages.filter(p => p.packageType === 'annual').map((pkg) => (
                      <div key={pkg.id} className="col-md-4">
                        <div className={`card h-100 ${!pkg.isActive ? 'opacity-50' : ''}`} style={{ borderLeft: '4px solid #10b981' }}>
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h5 className="mb-0" style={{ fontWeight: '600' }}>{pkg.name}</h5>
                                <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>Annual</span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <button onClick={() => handleEditClick(pkg)} className="btn btn-sm btn-link p-0" title="Edit" style={{ color: '#667eea' }}>
                                  <i className="bi bi-pencil" style={{ fontSize: '1.1rem' }}></i>
                                </button>
                                <button onClick={() => handleToggleStatus(pkg)} className="btn btn-sm btn-link p-0" title={pkg.isActive ? 'Deactivate' : 'Activate'} style={{ color: pkg.isActive ? '#f59e0b' : '#10b981' }}>
                                  <i className={`bi ${pkg.isActive ? 'bi-pause-circle' : 'bi-play-circle'}`} style={{ fontSize: '1.1rem' }}></i>
                                </button>
                                <button onClick={() => handleDeleteClick(pkg)} className="btn btn-sm btn-link p-0" title="Delete" style={{ color: '#ef4444' }}>
                                  <i className="bi bi-trash" style={{ fontSize: '1.1rem' }}></i>
                                </button>
                                <span className={`badge ${pkg.isActive ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.75rem' }}>{pkg.isActive ? 'Active' : 'Inactive'}</span>
                              </div>
                            </div>
                            <div className="mb-3">
                              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>{formatCurrency(pkg.price, pkg.currency)}<span style={{ fontSize: '0.875rem', fontWeight: '400', color: '#6b7280' }}> / year</span></div>
                            </div>
                            <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>{pkg.description || 'No description'}</p>
                            <div style={{ fontSize: '0.875rem' }}>
                              <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>Unlimited elections</div>
                              <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>{pkg.durationDays}-day subscription period</div>
                              {pkg.multiVotingEnabled && <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>Multi-voting</div>}
                              {pkg.customBrandingEnabled && <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>Custom branding</div>}
                              {pkg.advancedAnalyticsEnabled && <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>Advanced analytics</div>}
                              <div className="mb-2"><i className="bi bi-check-circle text-success me-2"></i>{pkg.supportLevel.charAt(0).toUpperCase() + pkg.supportLevel.slice(1)} support</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content" style={{ maxHeight: '90vh' }}>
              <div className="modal-header">
                <h5 className="modal-title">{editingPackage ? 'Edit Package' : 'Add Package'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body" style={{ maxHeight: 'calc(90vh - 140px)', overflowY: 'auto' }}>
                  <div className="mb-3">
                    <label className="form-label">Package Type *</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          type="radio"
                          name="packageType"
                          value="per_election"
                          checked={formData.packageType === 'per_election'}
                          onChange={handleInputChange}
                          className="form-check-input"
                          id="typePerElection"
                        />
                        <label className="form-check-label" htmlFor="typePerElection">
                          Per Election
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="radio"
                          name="packageType"
                          value="annual"
                          checked={formData.packageType === 'annual'}
                          onChange={handleInputChange}
                          className="form-check-input"
                          id="typeAnnual"
                        />
                        <label className="form-check-label" htmlFor="typeAnnual">
                          Annual Subscription
                        </label>
                      </div>
                    </div>
                    <small className="text-muted">
                      {formData.packageType === 'annual'
                        ? 'Annual subscriptions allow unlimited elections for the duration period'
                        : 'Per-election packages are selected and paid for each individual election'}
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Package Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                    />
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Currency *</label>
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleInputChange}
                        className="form-select"
                        required
                        style={{ height: '38px' }}
                      >
                        <option value="USD">USD ($)</option>
                        <option value="UGX">UGX (USh)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                        <option value="KES">KES (KSh)</option>
                        <option value="TZS">TZS (TSh)</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Price *</label>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  
                  {currencyChanged && (
                    <div className="alert alert-warning mb-3" role="alert">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <strong>Note:</strong> Changing the currency will update ALL packages to use {formData.currency}.
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="form-control"
                      rows="2"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Max Voters</label>
                    <input
                      type="number"
                      name="maxVoters"
                      value={formData.maxVoters}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Unlimited"
                      min="0"
                    />
                    <small className="text-muted">Empty = unlimited</small>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Max Positions</label>
                    <input
                      type="number"
                      name="maxPositions"
                      value={formData.maxPositions}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Unlimited"
                      min="0"
                    />
                    <small className="text-muted">Empty = unlimited</small>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Duration (days) *</label>
                    <input
                      type="number"
                      name="durationDays"
                      value={formData.durationDays}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                      min="1"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Support Level *</label>
                    <select
                      name="supportLevel"
                      value={formData.supportLevel}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                      style={{ height: '38px' }}
                    >
                      <option value="basic">Basic</option>
                      <option value="priority">Priority</option>
                      <option value="dedicated">Dedicated</option>
                    </select>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      name="displayOrder"
                      value={formData.displayOrder}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Features</label>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        name="multiVotingEnabled"
                        checked={formData.multiVotingEnabled}
                        onChange={handleInputChange}
                        className="form-check-input"
                        id="multiVoting"
                      />
                      <label className="form-check-label" htmlFor="multiVoting">
                        Enable Multi-Voting
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        name="customBrandingEnabled"
                        checked={formData.customBrandingEnabled}
                        onChange={handleInputChange}
                        className="form-check-input"
                        id="customBranding"
                      />
                      <label className="form-check-label" htmlFor="customBranding">
                        Enable Custom Branding
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        name="emailNotificationsEnabled"
                        checked={formData.emailNotificationsEnabled}
                        onChange={handleInputChange}
                        className="form-check-input"
                        id="emailNotifications"
                      />
                      <label className="form-check-label" htmlFor="emailNotifications">
                        Enable Email Notifications
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        name="advancedAnalyticsEnabled"
                        checked={formData.advancedAnalyticsEnabled}
                        onChange={handleInputChange}
                        className="form-check-input"
                        id="advancedAnalytics"
                      />
                      <label className="form-check-label" htmlFor="advancedAnalytics">
                        Enable Advanced Analytics
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingPackage ? 'Update' : 'Create'} Package
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showDeleteModal}
        title="Delete Package"
        message={`Are you sure you want to delete "${packageToDelete?.name}"? This action cannot be undone and will fail if the package is in use by any elections.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setPackageToDelete(null);
        }}
        confirmText="Delete"
        confirmClass="btn-danger"
      />
    </div>
  );
};

export default PricingPackages;
